import { useEffect, useState } from "react";
import { useTrackingContext } from "../../providers/TrackingProvider";

/**
 * Custom hook for generating and managing tracking IDs for analytics.
 *
 * Features:
 * - Generates unique tracking IDs for analytics events
 * - Integrates with TrackingProvider for centralized tracking management
 * - Supports opt-out functionality for privacy compliance
 * - Handles tracking overrides for testing or special cases
 * - Uses JSON stringification for consistent data serialization
 * - Automatically updates tracking ID when data or name changes
 * - Respects user privacy preferences through opt-out mechanism
 * - Provides async tracking ID generation
 *
 * @param props - Configuration object containing tracking parameters
 * @param props.name - The name identifier for the tracking event
 * @param props.data - The data object to track
 * @param props.hasOverride - Whether to override tracking behavior
 * @returns Generated tracking ID string or undefined if tracking is disabled
 */
export const useTrackingId = ({
  name,
  data,
  hasOverride,
}: {
  name: string;
  data: { [key: string]: unknown };
  hasOverride?: boolean;
}) => {
  const [trackingId, setTrackingId] = useState<string | undefined>(undefined);
  const { getTrackingId, optOut } = useTrackingContext();

  useEffect(() => {
    if (hasOverride || optOut) {
      return;
    }
    getTrackingId(JSON.stringify(data), name).then((result) =>
      setTrackingId(result),
    );
  }, [hasOverride, data, name, optOut, getTrackingId]);

  return trackingId;
};
